/**
 * Demo tenant service.
 *
 * The Demo Company is an ordinary tenant flagged with
 * `company_settings.is_demo = true` and registered in `demo_tenants`. It is
 * fully isolated: database triggers skip billing/automation side effects and
 * outbound edge functions refuse to contact real providers for demo data.
 *
 * Scenarios (`demo_scenarios`) allow additional demo tenants later (small
 * haulier, dealer group, enterprise fleet, …) reusing this same framework.
 */
import { supabase } from "@/integrations/supabase/client";

export interface DemoScenario {
  key: string;
  label: string;
  description: string | null;
  is_default: boolean;
}

export interface DemoTenant {
  company_id: string;
  scenario_key: string;
  label: string;
  last_seeded_at: string | null;
  seed_version: number;
}

export async function listDemoScenarios(): Promise<DemoScenario[]> {
  const { data } = await supabase
    .from("demo_scenarios")
    .select("key, name, description, is_default")
    .order("is_default", { ascending: false })
    .order("name");
  return (data ?? []).map((r) => ({
    key: r.key,
    label: r.name,
    description: r.description,
    is_default: r.is_default,
  }));
}

export async function listDemoTenants(): Promise<DemoTenant[]> {
  const { data } = await supabase
    .from("demo_tenants")
    .select("company_id, scenario_key, label, last_seeded_at, seed_version")
    .order("created_at");
  return (data ?? []) as DemoTenant[];
}

/** The primary demo company a user can explore, if one exists. */
export async function getPrimaryDemoTenant(): Promise<DemoTenant | null> {
  const tenants = await listDemoTenants();
  return tenants[0] ?? null;
}

/** Is this company the isolated demo tenant? */
export async function isDemoCompanyId(companyId: string | null): Promise<boolean> {
  if (!companyId) return false;
  const { data } = await supabase
    .from("company_settings")
    .select("is_demo")
    .eq("company_id", companyId)
    .maybeSingle();
  return data?.is_demo === true;
}

/** Join the demo company as an additional membership (never leaves own company). */
export async function joinDemoCompany(companyId: string): Promise<void> {
  const { error } = await supabase.rpc("join_demo_company", { _company_id: companyId });
  if (error) throw error;
}

/** Remove the caller's demo membership. */
export async function leaveDemoCompany(companyId: string): Promise<void> {
  const { error } = await supabase.rpc("leave_demo_company", { _company_id: companyId });
  if (error) throw error;
}

type AdminAction = "provision" | "reset" | "reseed";

/** Super Admin demo management — executed server side with service role. */
export async function runDemoAdminAction(
  action: AdminAction,
  params: { companyId?: string; name?: string; scenario?: string } = {},
): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.functions.invoke("demo-admin", {
    body: { action, ...params },
  });
  if (error) throw error;
  if (data?.error) throw new Error(String(data.error));
  return data as Record<string, unknown>;
}
